import { log } from '../log';
import { getTabsThatCanBeCommunicateWith } from '../utils';
import { isWorkspaceLinkingBridgeAction, publishLinkIntentFromLegacy } from '../workspaceBridge';
import { MATRIX } from './matrix';
import { notUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import { all, put, takeEvery } from 'redux-saga/effects';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { UserDoubleClickedAccountFromTableAction } from 'src/store/actions/widgets/accounts';
import { updateExecutionsAccount } from 'src/store/actions/widgets/executions';
import type { WorkspaceLinkingAccountSelectedAction } from 'src/store/actions/widgets/linking';
import { updateLocatesAccount } from 'src/store/actions/widgets/locates';
import { updateLocatesV2Account } from 'src/store/actions/widgets/locatesV2';
import { updateMessagesAccount } from 'src/store/actions/widgets/messages';
import { updateOrderBlotterAccount } from 'src/store/actions/widgets/orderBlotter';
import type { UserDoubleClickedOrderFromBlotterAction } from 'src/store/actions/widgets/orderBlotter';
import { updateOrdersAccount } from 'src/store/actions/widgets/orders';
import { updatePositionsAccount } from 'src/store/actions/widgets/positions';
import type { UserDoubleClickedPositionFromTableAction } from 'src/store/actions/widgets/positions';
import { updateTradeProAccount } from 'src/store/actions/widgets/tradePro';
import type { UserSelectedTradeProAccountAction } from 'src/store/actions/widgets/tradePro';

export function* handleUserSelectedAccount(
    action:
        | UserDoubleClickedAccountFromTableAction
        | UserDoubleClickedPositionFromTableAction
        | UserSelectedTradeProAccountAction
        | UserDoubleClickedOrderFromBlotterAction
        | WorkspaceLinkingAccountSelectedAction,
) {
    let accountId: string;

    switch (action.type) {
        case 'userDoubleClickedAccountFromTable':
            accountId = action.accountId;
            break;
        case 'userDoubleClickedPositionFromTable':
            // Only proceed if accountId is present for position actions
            if (!action.accountId) return;
            accountId = action.accountId;
            break;
        case 'userSelectedTradeProAccount':
            accountId = action.accountId;
            break;
        case 'userDoubleClickedOrderFromBlotter':
            // Only proceed if accountId is present for blotter actions
            if (!action.accountId) return;
            accountId = action.accountId;
            break;
        case 'workspaceLinkingAccountSelected':
            accountId = action.accountId;
            break;
        default:
            throw new UnreachableCaseError(action);
    }

    if (!isWorkspaceLinkingBridgeAction(action)) {
        yield* publishLinkIntentFromLegacy({
            type: 'account-selected',
            fromTabId: action.tabId,
            details: { accountId },
        });
    }

    const widgetsThatShouldReceiveUpdates = yield* getTabsThatCanBeCommunicateWith(action.tabId, MATRIX);
    if (!widgetsThatShouldReceiveUpdates.length) return;

    const widgetUpdates = widgetsThatShouldReceiveUpdates
        .map((x) => {
            const type = x.widget.type;
            switch (type) {
                case 'order-blotter':
                    return updateOrderBlotterAccount(x.id, [accountId]);
                case 'messages':
                    return updateMessagesAccount(x.id, [accountId]);
                case 'positions':
                    return updatePositionsAccount(x.id, accountId);
                case 'trade-pro':
                case 'trade-pro-multi':
                    return updateTradeProAccount(x.id, accountId);
                case 'executions':
                    return updateExecutionsAccount(x.id, accountId);
                case 'orders':
                    return updateOrdersAccount(x.id, accountId);
                case 'locates':
                    return updateLocatesAccount(x.id, accountId);
                case 'lab':
                    return updateLocatesV2Account(x.id, accountId);
                case 'accounts':
                case 'alpha-lens':
                case 'events':
                case 'filings':
                case 'news':
                case 'manual-review':
                case 'scanner':
                case 'screener':
                case 'searchalpha':
                case 'results':
                case 'table':
                case 'table-v2':
                case 'watchlist':
                case 'basket-trading':
                case 'alpha-eye':
                case 'notepad':
                case 'platforms':
                case 'symbols':
                case 'traders':
                case 'alerts':
                case 'tradingview-chart':
                case 'backtest':
                    if (TRACE) {
                        log.trace({
                            message: 'account change for widget not handled',
                            widget: x.widget,
                            accountId,
                        });
                    }
                    return undefined;
                default:
                    throw new UnreachableCaseError(type);
            }
        })
        .filter(notUndefined);
    if (widgetUpdates.length === 0) return;

    yield all(widgetUpdates.map((x) => put(x)));
}

export function* accountsLinkingSagas() {
    yield all([
        takeEvery('userDoubleClickedAccountFromTable', handleUserSelectedAccount),
        takeEvery('userDoubleClickedPositionFromTable', handleUserSelectedAccount),
        takeEvery('userSelectedTradeProAccount', handleUserSelectedAccount),
        takeEvery('userDoubleClickedOrderFromBlotter', handleUserSelectedAccount),
        takeEvery('workspaceLinkingAccountSelected', handleUserSelectedAccount),
    ]);
}
